import { LogLevel, Configuration, InteractionRequiredAuthError, AuthenticationResult } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useRef } from "react";

// Browser check variables
// If you support IE, our recommendation is that you sign-in using Redirect APIs
// If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
const ua = window.navigator.userAgent;
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const firefox = ua.indexOf("Firefox");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0; // Only needed if you need to support the redirect flow in Firefox incognito

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: "B2C_1_signupsignin",
    },
    authorities: {
        signUpSignIn: {
            authority: "https://cubecon.b2clogin.com/cubecon.onmicrosoft.com/B2C_1_signupsignin"
        }
    },
    authorityDomain: "cubecon.b2clogin.com"
}

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
    auth: {
        clientId: "62a23017-b6ff-419d-a534-e23418e4d3cd", //process.env.REACT_APP_CLIENT_ID ?? "c090126d-21bb-49fe-9379-ceba5f1981aa",
        authority: b2cPolicies.authorities.signUpSignIn.authority,
        knownAuthorities: [b2cPolicies.authorityDomain],
        redirectUri: "/",
        postLogoutRedirectUri: "/",
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: isIE || isEdge || isFirefox,
    },
    system: {
        allowNativeBroker: false, // Disables WAM Broker
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        //console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        //console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

export interface IUseAuth {
	beforeQuery: (func: () => void) => void;
	onSuccess: (func: (tokenResponse: AuthenticationResult) => void) => void;
	acquireToken: (beforeQuery?: () => void) => Promise<AuthenticationResult | undefined>;
}

export function useAuth(requireLogin: boolean = false): IUseAuth {
	const { instance, inProgress } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	
	const before = useRef<() => void>();
	const success = useRef<(tokenResponse: AuthenticationResult) => void>();

	const beforeQuery = useCallback((newFunc: () => void) => {
		before.current = newFunc; 
	}, []);

	const onSuccess = useCallback((newFunc: (tokenResponse: AuthenticationResult) => void) => {
		success.current = newFunc;
	}, []);

	const queryMsal = useCallback((canRedirect: boolean, beforeTokenQuery?: ()=> void): Promise<AuthenticationResult | undefined> => {
		if (inProgress === 'none') {
			if (canRedirect && !isAuthenticated) {
				instance.loginRedirect(loginRequest);
			}
			else {
				const account = instance.getAllAccounts()[0];
				if (account) {
					if (beforeTokenQuery) {
						beforeTokenQuery();
					}						
					return instance.acquireTokenSilent({
							...loginRequest,
							account: account,
						}).catch((error) => {
							if ((error instanceof InteractionRequiredAuthError) && canRedirect) {
								instance.acquireTokenRedirect({ ...loginRequest });
							}
							return undefined;
						});
				}	
			}
		}
		return Promise.resolve(undefined);
	}, [instance, inProgress, isAuthenticated]);

	useEffect(() => {
		queryMsal(requireLogin, before.current).then((tokenResponse?: AuthenticationResult) => {
			if (tokenResponse && success.current) {
				success.current(tokenResponse);
			}
		});
		}, [queryMsal, before, success, requireLogin]);

	const acquireToken = useCallback((beforeQuery?: ()=> void): Promise<AuthenticationResult | undefined> => {
		return queryMsal(true, beforeQuery);
	}, [queryMsal])

	return { beforeQuery, onSuccess, acquireToken };
}

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
    scopes: ["https://cubecon.onmicrosoft.com/62a23017-b6ff-419d-a534-e23418e4d3cd/files.read"]
};
